import { toast } from 'react-toastify';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import { Chip } from '@mui/material';
import {
  useGenerateFilterKTreeData,
  useGenerateKTreeData,
  useGetKtreeDefinitionDetails,
  useGetKTreeGenerationFilter,
} from 'services/v1/Tenant/AROKMS/KTreeDataService';
import { useParams } from 'react-router-dom';
import React, { useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
import { useGetCustomRuleDropdownFormData } from 'services/v1/Tenant/AROKMS/CustomRuleService';
import { CustomRuleConditionDropdownItem } from 'types/api/Tenant/AROKMS/CustomRuleTypes';
import { ConditionField } from '../../DataInput/components/Conditional';
import { CUSTOM_RULE_OPERATOR_VALUE } from '../../DataInput/components/CustomRuleDetail';
import { getErrorMessage } from 'utils/Error';

const ModalContentStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 950,
  maxHeight: '85vh',
  bgcolor: 'background.paper',
  borderRadius: 2,
  px: 0,
  py: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const headerStyle = {
  px: 3,
  pt: 2.5,
  pb: 2,
  bgcolor: '#F8FAFC',
  borderBottom: '1px solid #E3EBF6',
};

const contentStyle = {
  px: 3,
  py: 2.5,
  flex: 1,
  overflow: 'auto',
  minHeight: 200,
  maxHeight: 'calc(85vh - 200px)',
};

const footerStyle = {
  px: 3,
  py: 2,
  borderTop: '1px solid #E3EBF6',
  bgcolor: '#FBFBFB',
};

const conditionRowStyle = {
  bgcolor: '#FFFFFF',
  border: '1px solid #E3EBF6',
  borderRadius: 1.5,
  px: 2,
  py: 1.5,
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    borderColor: '#42BB93',
    boxShadow: '0 1px 4px rgba(66, 187, 147, 0.1)',
  },
};

const conditionConnectorStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  py: 0.5,
};

const andChipStyle = {
  fontSize: '12px',
  fontWeight: 700,
  height: '24px',
  bgcolor: '#E8F5E9',
  color: '#2E7D32',
  border: '1px solid #A5D6A7',
};

const orChipStyle = {
  fontSize: '12px',
  fontWeight: 700,
  height: '24px',
  bgcolor: '#FFF3E0',
  color: '#E65100',
  border: '1px solid #FFCC80',
};

const closeIconStyle = { color: '#98A2AE', cursor: 'pointer' };

const filterIconStyle = { color: '#3B4797', fontSize: 28 };

const summaryContainerStyle = {
  mt: 2.5,
  p: 2,
  bgcolor: '#F8FAFC',
  border: '1px solid #E3EBF6',
  borderRadius: 1.5,
};

const summaryTextStyle = {
  fontSize: 13,
  lineHeight: 1.8,
  color: '#333',
  fontFamily: '"Nunito", "Helvetica", "Arial", sans-serif',
};

let conditionIdCounter = 0;
function generateConditionId() {
  conditionIdCounter += 1;
  return `condition-${Date.now()}-${conditionIdCounter}`;
}

interface FormikInitialValues {
  conditions: {
    id: string;
    operator: string | null;
    field: string;
    operatorValue: string;
    operatorType: string;
    value: string;
  }[];
}

const formikInitialValues: FormikInitialValues = {
  conditions: [
    {
      id: generateConditionId(),
      operator: null,
      field: '',
      operatorValue: '',
      operatorType: '',
      value: '',
    },
  ],
};

export interface ModalFilterKTreeProps {
  visible?: boolean;
  title?: string;
  onClose?: () => void;
  ktreeTableId?: string;
}

export default function ModalFilterKTree(props: ModalFilterKTreeProps) {
  const { kTreeId } = useParams<{ kTreeId: string }>();
  const { data: kreeDefinitionDetails } = useGetKtreeDefinitionDetails(kTreeId);
  const { data: ktreeGenerationFilter } = useGetKTreeGenerationFilter(kTreeId);
  const { mutate: generateFilterKTreeData, isLoading: isSavingFilter } = useGenerateFilterKTreeData();
  const { mutate: generateKtreeData, isLoading: isGenerating } = useGenerateKTreeData();

  const { data: dropdownFormData } = useGetCustomRuleDropdownFormData(
    kreeDefinitionDetails?.data?.subject?.id?.toString(),
    true,
  );

  const { onClose, visible = false, title = 'Filter Hierarchy Generation' } = props;

  const formik = useFormik({
    initialValues: formikInitialValues,
    onSubmit: () => {
      handleOnSubmit();
    },
  });

  const isLastCondition = (index: number) => index === formik.values.conditions.length - 1;
  const isBusy = isSavingFilter || isGenerating;
  const hasExistingFilter = Boolean(ktreeGenerationFilter?.data?.filterValue);
  const conditionCount = formik.values.conditions.length;

  const handleOnSubmit = () => {
    if (kTreeId) {
      generateFilterKTreeData(
        {
          id: kTreeId,
          filterValue: JSON.stringify(formik.values.conditions),
        },
        {
          onSuccess: () => {
            handleOnGenerateKtreeData();
            formik.resetForm();
            onClose?.();
          },
        },
      );
    }
  };

  const addCondition = (operator: string) => {
    const newCondition = {
      id: generateConditionId(),
      operator: null,
      operatorType: operator,
      field: '',
      operatorValue: '',
      value: '',
    };
    formik.setFieldValue('conditions', [...formik.values.conditions, newCondition]);
  };

  const removeCondition = (index: number) => {
    const newConditions = [...formik.values.conditions];
    newConditions.splice(index, 1);
    formik.setFieldValue('conditions', newConditions);
  };

  const handleClose = () => {
    if (isBusy) return;
    formik.resetForm();
    onClose?.();
  };

  const handleOnResetFilter = () => {
    if (kTreeId) {
      generateFilterKTreeData(
        {
          id: kTreeId,
          resetFilter: true,
          filterValue: null,
        },
        {
          onSuccess: () => {
            handleOnGenerateKtreeData();
            handleClose();
          },
        },
      );
    }
  };

  const handleOnGenerateKtreeData = () => {
    const toastId = toast.loading('Building KTree data...');
    if (kTreeId) {
      generateKtreeData(
        { id: kTreeId },
        {
          onSuccess: () => {
            toast.update(toastId, {
              render: 'Build data success!',
              type: 'success',
              isLoading: false,
              autoClose: 3000,
            });
          },
          onError: (error: any) => {
            toast.update(toastId, { render: getErrorMessage(error), type: 'error', isLoading: false, autoClose: 3000 });
          },
        },
      );
    }
  };

  const fieldData: CustomRuleConditionDropdownItem[] = useMemo(() => {
    if (dropdownFormData) {
      return dropdownFormData.data.allAttributeOptions;
    }
    return [];
  }, [dropdownFormData]);

  const ruleSummary = useMemo(() => {
    const { conditions } = formik.values;
    const hasAnyComplete = conditions.some(
      (c) => c.field && c.operator && (c.value || c.operator === CUSTOM_RULE_OPERATOR_VALUE.NOT_EMPTY),
    );
    if (!hasAnyComplete) return null;

    // Group conditions by OR segments — within each group, items are joined by AND
    const groups: (typeof conditions)[] = [];
    let currentGroup: typeof conditions = [];

    conditions.forEach((condition, index) => {
      if (index > 0 && condition.operatorType === 'OR') {
        groups.push(currentGroup);
        currentGroup = [];
      }
      currentGroup.push(condition);
    });
    groups.push(currentGroup);

    return groups
      .map((group) => {
        const parts = group
          .filter((c) => c.field && c.operator)
          .map((c) => {
            const isNotEmpty = c.operator === CUSTOM_RULE_OPERATOR_VALUE.NOT_EMPTY;
            if (isNotEmpty) {
              return `"${c.field}" is not empty`;
            }
            return `"${c.field}" ${(c.operator || '').toLowerCase()} "${c.value}"`;
          });
        return parts.join(' AND ');
      })
      .filter((g) => g.length > 0)
      .join(' OR ');
  }, [formik.values]);

  useEffect(() => {
    if (ktreeGenerationFilter?.data && visible) {
      if (ktreeGenerationFilter.data.filterValue) {
        try {
          formik.setValues({
            conditions: JSON.parse(ktreeGenerationFilter.data.filterValue),
          });
        } catch {
          // If stored filter is malformed, keep default empty condition
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ktreeGenerationFilter?.data, visible]);

  return (
    <Modal open={visible} onClose={handleClose}>
      <Stack direction='column' sx={ModalContentStyle}>
        {/* ── Header ── */}
        <Box sx={headerStyle}>
          <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
            <Stack direction='row' spacing={1.5} alignItems='center'>
              <FilterListRoundedIcon sx={filterIconStyle} />
              <Stack>
                <Typography variant='modal-title' component='h2'>
                  {title}
                </Typography>
                <Typography variant='input-label-gray' fontSize={13}>
                  Define conditions to generate a hierarchy only for matching data.
                </Typography>
              </Stack>
            </Stack>
            <IconButton onClick={handleClose} size='small' disabled={isBusy}>
              <HighlightOffRoundedIcon sx={closeIconStyle} />
            </IconButton>
          </Stack>
        </Box>

        {/* ── Content ── */}
        <Box sx={contentStyle}>
          {/* Section Label */}
          <Stack direction='row' alignItems='center' justifyContent='space-between' mb={2}>
            <Stack direction='row' alignItems='center' spacing={1}>
              <Typography variant='input-label' fontWeight={700}>
                Filter Conditions
              </Typography>
              <Chip
                label={`${conditionCount} ${conditionCount === 1 ? 'rule' : 'rules'}`}
                size='small'
                sx={{ fontSize: '12px', height: '22px', bgcolor: '#E3EBF6', color: '#3B4797', fontWeight: 600 }}
              />
            </Stack>
            {hasExistingFilter && (
              <Chip
                label='Active filter applied'
                size='small'
                sx={{ fontSize: '12px', height: '22px', bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600 }}
              />
            )}
          </Stack>

          {/* Condition Rows */}
          <form onSubmit={formik.handleSubmit}>
            <Stack spacing={0}>
              {formik.values.conditions.map((condition, index) => (
                <React.Fragment key={condition.id}>
                  {/* AND/OR Connector */}
                  {index !== 0 && (
                    <Box sx={conditionConnectorStyle}>
                      {condition.operatorType === 'AND' && <Chip label='AND' size='small' sx={andChipStyle} />}
                      {condition.operatorType === 'OR' && <Chip label='OR' size='small' sx={orChipStyle} />}
                    </Box>
                  )}

                  {/* Condition Card */}
                  <Box sx={conditionRowStyle}>
                    <Stack
                      direction='row'
                      alignItems='center'
                      spacing={1}
                      mb={index === 0 && conditionCount === 1 ? 0 : 0}
                    >
                      {conditionCount > 1 && (
                        <Typography
                          variant='input-label-gray'
                          fontSize={12}
                          fontWeight={700}
                          sx={{ minWidth: 20, textAlign: 'center' }}
                        >
                          {index + 1}
                        </Typography>
                      )}
                      <Box sx={{ flex: 1 }}>
                        <ConditionField
                          condition={condition}
                          index={index}
                          isLast={isLastCondition(index)}
                          fieldData={fieldData}
                          formik={formik}
                          onRemove={removeCondition}
                          onAdd={addCondition}
                        />
                      </Box>
                    </Stack>
                  </Box>
                </React.Fragment>
              ))}
            </Stack>
          </form>

          {/* Helpful hint */}
          {conditionCount === 1 && !formik.values.conditions[0]?.field && (
            <Alert
              severity='info'
              variant='outlined'
              sx={{ mt: 2, fontSize: 13 }}
              icon={<AddCircleOutlineRoundedIcon fontSize='small' />}
            >
              Select an attribute, operator, and value to create your first filter condition. Use AND/OR buttons to add
              more rules.
            </Alert>
          )}

          {/* Rule Summary */}
          {ruleSummary && (
            <Box sx={summaryContainerStyle}>
              <Stack direction='row' alignItems='center' spacing={1} mb={1}>
                <SummarizeOutlinedIcon sx={{ fontSize: 18, color: '#3B4797' }} />
                <Typography variant='input-label' fontWeight={700} fontSize={13}>
                  Rule Summary
                </Typography>
              </Stack>
              <Typography sx={summaryTextStyle}>Generate hierarchy where {ruleSummary}.</Typography>
            </Box>
          )}
        </Box>

        {/* ── Footer ── */}
        <Box sx={footerStyle}>
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            {/* Left side - Reset */}
            <Button
              variant='main-table-panel-borderless'
              startIcon={<DeleteOutlineRoundedIcon />}
              onClick={handleOnResetFilter}
              disabled={isBusy || !hasExistingFilter}
              sx={{ fontSize: 13, color: '#BB4942' }}
            >
              Reset Filter
            </Button>

            {/* Right side - Cancel & Save */}
            <Stack direction='row' spacing={1.5}>
              <Button variant='main-table-panel-border' onClick={handleClose} disabled={isBusy} sx={{ minWidth: 100 }}>
                Cancel
              </Button>
              <Button variant='main-table-panel' onClick={handleOnSubmit} disabled={isBusy} sx={{ minWidth: 140 }}>
                {isBusy ? 'Applying...' : 'Apply Filter'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Modal>
  );
}
